import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Clock, AlertTriangle, CheckCircle, Loader2, Play } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

interface DeactivatedUser {
  id: string;
  first_name: string;
  last_name: string;
  deactivated_at: string;
  daysRemaining: number;
}

interface DeletionLog {
  id: string;
  execution_date: string;
  users_found: number;
  users_processed: number;
  users_deleted: number;
  total_records_deleted: number;
  status: string;
  triggered_by: string;
  processing_time_ms: number;
  errors: string[] | null;
  completed_at: string;
}

export function AccountCleanupMonitor() {
  const [deactivatedUsers, setDeactivatedUsers] = useState<DeactivatedUser[]>([]);
  const [deletionLogs, setDeletionLogs] = useState<DeletionLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTriggering, setIsTriggering] = useState(false);
  const { toast } = useToast();

  const fetchDeactivatedUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, deactivated_at')
        .eq('is_active', false)
        .order('deactivated_at', { ascending: true });

      if (error) throw error;

      const usersWithCountdown = data?.map(user => {
        const deactivatedDate = new Date(user.deactivated_at);
        const deletionDate = new Date(deactivatedDate);
        deletionDate.setDate(deletionDate.getDate() + 7);
        const now = new Date();
        const daysRemaining = Math.ceil((deletionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        return {
          ...user,
          daysRemaining: Math.max(0, daysRemaining)
        };
      }) || [];

      setDeactivatedUsers(usersWithCountdown);
    } catch (error) {
      console.error('Error fetching deactivated users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch deactivated users",
        variant: "destructive",
      });
    }
  };

  const fetchDeletionLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('account_deletion_logs')
        .select('*')
        .order('execution_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      setDeletionLogs(data || []);
    } catch (error) {
      console.error('Error fetching deletion logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch deletion logs",
        variant: "destructive",
      });
    }
  };

  const triggerManualCleanup = async () => {
    setIsTriggering(true);
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-deleted-accounts', {
        body: { trigger: 'manual' }
      });

      if (error) throw error;

      toast({
        title: "Cleanup Triggered",
        description: data.message || "Account cleanup has been triggered successfully",
      });

      // Refresh both lists
      await fetchDeactivatedUsers();
      await fetchDeletionLogs();
    } catch (error) {
      console.error('Error triggering cleanup:', error);
      toast({
        title: "Error",
        description: "Failed to trigger account cleanup",
        variant: "destructive",
      });
    } finally {
      setIsTriggering(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchDeactivatedUsers(), fetchDeletionLogs()]);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500';
      case 'completed_with_errors': return 'bg-amber-500';
      case 'running': return 'bg-blue-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-muted';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'completed_with_errors': return <AlertTriangle className="h-4 w-4" />;
      case 'running': return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'error': return <AlertTriangle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Account Cleanup Monitor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading cleanup data...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card with Manual Trigger */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Account Cleanup Monitor
              </CardTitle>
              <CardDescription>
                Automated cleanup runs daily at 2 AM UTC. Accounts are deleted 7 days after deactivation.
              </CardDescription>
            </div>
            <Button 
              onClick={triggerManualCleanup} 
              disabled={isTriggering}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isTriggering ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Manual Trigger
            </Button>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Deletions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Pending Deletions ({deactivatedUsers.length})
            </CardTitle>
            <CardDescription>
              Deactivated accounts waiting for permanent deletion
            </CardDescription>
          </CardHeader>
          <CardContent>
            {deactivatedUsers.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No deactivated accounts pending deletion
              </p>
            ) : (
              <div className="space-y-3">
                {deactivatedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div>
                      <p className="font-medium">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Deactivated {formatDistanceToNow(new Date(user.deactivated_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="text-right">
                      {user.daysRemaining <= 0 ? (
                        <Badge variant="destructive">Ready for deletion</Badge>
                      ) : (
                        <Badge variant="secondary">
                          {user.daysRemaining} day{user.daysRemaining !== 1 ? 's' : ''} remaining
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Cleanup History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Cleanup History
            </CardTitle>
            <CardDescription>
              Recent account cleanup executions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {deletionLogs.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No cleanup history available
              </p>
            ) : (
              <div className="space-y-3">
                {deletionLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1 rounded-full text-white ${getStatusColor(log.status)}`}>
                        {getStatusIcon(log.status)}
                      </div>
                      <div>
                        <p className="font-medium">
                          {format(new Date(log.execution_date), 'MMM dd, yyyy HH:mm')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {log.triggered_by} • {log.processing_time_ms ? `${log.processing_time_ms}ms` : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {log.users_deleted}/{log.users_found} users
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {log.total_records_deleted} records deleted
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Error Details */}
      {deletionLogs.some(log => log.errors && log.errors.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Recent Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {deletionLogs
                .filter(log => log.errors && log.errors.length > 0)
                .slice(0, 3)
                .map((log) => (
                  <Alert key={log.id} variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-medium mb-1">
                        {format(new Date(log.execution_date), 'MMM dd, yyyy HH:mm')}
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {log.errors?.map((error, index) => (
                          <li key={index} className="text-sm">{error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}